import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { User } from '@vyce/core/src/types';
import {
  getFormTemplatesRequest,
  deleteFormTemplateRequest,
  duplicateFormTemplateRequest,
  createFormFromTemplateRequest,
  createFormFromTemplateForFillInRequest,
  archiveFormTemplateRequest,
  getFormTemplateFoldersRequest,
  createFormTemplateFolderRequest,
  updateFormTemplateFolderRequest,
  archiveFormTemplateFolderRequest,
  moveFormTemplateFolderRequest,
  deleteFormTemplateFolderRequest,
  addFormTemplateToFolderRequest,
  removeFormTemplateFromFolderRequest,
} from '@vyce/core/src/api/forms';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import type { FormTemplateDoc, FormTemplateFolderDoc } from '@vyce/core/src/api/types';

const TEMPLATE_FOLDERS_FETCH_LIMIT = 500;
const FOLDER_VIEW_TEMPLATES_FETCH_LIMIT = 500;

export const useFormTemplates = ({ user, companyId }: { user: User; companyId: string }) => {
  const [templates, setTemplates] = useState<FormTemplateDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [folders, setFolders] = useState<FormTemplateFolderDoc[]>([]);
  const [folderTemplatesMap, setFolderTemplatesMap] = useState<Record<string, FormTemplateDoc[]>>({});
  const [unassignedTemplates, setUnassignedTemplates] = useState<FormTemplateDoc[]>([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [loadingFolderView, setLoadingFolderView] = useState(false);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const showNotificationRef = useRef(showNotification);
  const handleServerErrorRef = useRef(handleServerError);

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    handleServerErrorRef.current = handleServerError;
  }, [handleServerError]);

  const getFormTemplates = useCallback(
    async (opts?: {
      offset?: number;
      search?: string;
      folderId?: string | null;
      archived?: boolean;
    }) => {
      try {
        setLoading(true);
        const res = await getFormTemplatesRequest({
          offset: opts?.offset ?? 0,
          limit: 100,
          companyId,
          userId: user.uuid,
          search: opts?.search || null,
          folderId: opts?.folderId,
          archived: opts?.archived,
        });

        setTemplates(res.templates);
        setTotal(res.total ?? res.templates.length);
      } catch (e) {
        handleServerErrorRef.current(e);
      } finally {
        setLoading(false);
      }
    },
    [companyId, user.uuid]
  );

  const deleteTemplate = useCallback(
    async (templateId: string) => {
      try {
        await deleteFormTemplateRequest({ companyId, templateId, userId: user.uuid });
        showNotificationRef.current({ message: 'Form template has been deleted', options: { variant: 'success' } });
        await getFormTemplates({});
      } catch (e) {
        handleServerErrorRef.current(e);
      }
    },
    [companyId, user.uuid, getFormTemplates]
  );

  const duplicateTemplate = useCallback(
    async (templateId: string) => {
      try {
        const res = await duplicateFormTemplateRequest({
          companyId,
          templateId,
          userId: user.uuid,
          userName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || undefined,
        });
        showNotificationRef.current({ message: 'Form template has been duplicated', options: { variant: 'success' } });
        await getFormTemplates({});
        return res?.templateId;
      } catch (e) {
        handleServerErrorRef.current(e);
        return null;
      }
    },
    [companyId, user, getFormTemplates]
  );

  const createFormFromTemplate = useCallback(
    async (templateId: string) => {
      try {
        const res = await createFormFromTemplateRequest({
          companyId,
          templateId,
          userId: user.uuid,
          userName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || undefined,
          saveAs: 'form',
        });
        return res?.formId ?? null;
      } catch (e) {
        handleServerErrorRef.current(e);
        return null;
      }
    },
    [companyId, user.uuid, user?.first_name, user?.last_name]
  );

  const createFormFromTemplateForFillIn = useCallback(
    async (templateId: string) => {
      try {
        const res = await createFormFromTemplateForFillInRequest({
          companyId,
          templateId,
          userId: user.uuid,
          userName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || undefined,
          userEmail: user?.email || null,
          userPhone: user?.phone || null,
        });
        return res?.formId ?? null;
      } catch (e) {
        handleServerErrorRef.current(e);
        return null;
      }
    },
    [companyId, user.uuid, user?.first_name, user?.last_name, user?.email, user?.phone]
  );

  const archiveTemplate = useCallback(
    async (templateId: string, archived: boolean) => {
      try {
        await archiveFormTemplateRequest({ companyId, templateId, userId: user.uuid, archived });
        showNotificationRef.current({
          message: archived ? 'Form template archived' : 'Form template unarchived',
          options: { variant: 'success' },
        });
      } catch (e) {
        handleServerErrorRef.current(e);
      }
    },
    [companyId, user.uuid]
  );

  const getTemplateFolders = useCallback(
    async (opts?: { search?: string | null; archived?: boolean; parentId?: string | null }) => {
      const res = await getFormTemplateFoldersRequest({
        companyId,
        userId: user.uuid,
        search: opts?.search ?? null,
        archived: opts?.archived,
        parentId: opts?.parentId,
        limit: TEMPLATE_FOLDERS_FETCH_LIMIT,
      });
      return res.folders;
    },
    [companyId, user.uuid]
  );

  const createFolder = useCallback(
    async (name: string, colour: string | null, parentId: string | null = null) => {
      return createFormTemplateFolderRequest({
        companyId,
        userId: user.uuid,
        name,
        colour,
        parentId,
      });
    },
    [companyId, user.uuid]
  );

  const updateFolder = useCallback(
    async (folderId: string, payload: { name?: string; colour?: string | null }) => {
      return updateFormTemplateFolderRequest({
        companyId,
        folderId,
        userId: user.uuid,
        ...payload,
      });
    },
    [companyId, user.uuid]
  );

  const archiveFolder = useCallback(
    async (folderId: string, archived: boolean) => {
      return archiveFormTemplateFolderRequest({
        companyId,
        folderId,
        userId: user.uuid,
        archived,
      });
    },
    [companyId, user.uuid]
  );

  const moveFolder = useCallback(
    async (folderId: string, newParentId: string | null) => {
      return moveFormTemplateFolderRequest({
        companyId,
        folderId,
        newParentId,
        userId: user.uuid,
      });
    },
    [companyId, user.uuid]
  );

  const deleteFolder = useCallback(
    async (folderId: string, mode: 'blockIfChildrenOrForms' | 'cascade' = 'cascade') => {
      return deleteFormTemplateFolderRequest({
        companyId,
        folderId,
        userId: user.uuid,
        mode,
      });
    },
    [companyId, user.uuid]
  );

  const addTemplateToFolder = useCallback(
    async (templateId: string, folderId: string) => {
      return addFormTemplateToFolderRequest({
        companyId,
        templateId,
        folderId,
        userId: user.uuid,
      });
    },
    [companyId, user.uuid]
  );

  const removeTemplateFromFolder = useCallback(
    async (templateId: string) => {
      return removeFormTemplateFromFolderRequest({
        companyId,
        templateId,
        userId: user.uuid,
      });
    },
    [companyId, user.uuid]
  );

  const reloadFoldersBoard = useCallback(
    async (opts?: { archivedFolders?: boolean; archivedTemplates?: boolean; folderSearch?: string }) => {
      try {
        setLoadingFolderView(true);
        const fetchedFolders = await getTemplateFolders({
          search: opts?.folderSearch || null,
          archived: opts?.archivedFolders,
        });
        setFolders(fetchedFolders);

        const res = await getFormTemplatesRequest({
          offset: 0,
          limit: FOLDER_VIEW_TEMPLATES_FETCH_LIMIT,
          companyId,
          userId: user.uuid,
          archived: opts?.archivedTemplates,
        });

        const nextMap: Record<string, FormTemplateDoc[]> = {};
        fetchedFolders.forEach(folder => {
          nextMap[folder.id] = res.templates.filter(t => t.folderId === folder.id);
        });
        setFolderTemplatesMap(nextMap);
      } catch (error) {
        handleServerErrorRef.current(error);
      } finally {
        setLoadingFolderView(false);
      }
    },
    [companyId, user.uuid, getTemplateFolders]
  );

  const refreshUnassigned = useCallback(
    async (opts?: { archived?: boolean; search?: string }) => {
      try {
        setLoadingUnassigned(true);
        const res = await getFormTemplatesRequest({
          companyId,
          userId: user.uuid,
          offset: 0,
          limit: 100,
          folderId: 'unassigned',
          archived: opts?.archived,
          search: opts?.search || null,
        });
        setUnassignedTemplates(res.templates);
        setUnassignedTotal(res.total);
      } catch (error) {
        handleServerErrorRef.current(error);
      } finally {
        setLoadingUnassigned(false);
      }
    },
    [companyId, user.uuid]
  );

  return {
    templates,
    loading,
    getFormTemplates,
    total,
    deleteTemplate,
    duplicateTemplate,
    createFormFromTemplate,
    createFormFromTemplateForFillIn,
    archiveTemplate,
    folders,
    setFolders,
    folderTemplatesMap,
    setFolderTemplatesMap,
    unassignedTemplates,
    setUnassignedTemplates,
    unassignedTotal,
    loadingFolderView,
    loadingUnassigned,
    getTemplateFolders,
    createFolder,
    updateFolder,
    archiveFolder,
    moveFolder,
    deleteFolder,
    addTemplateToFolder,
    removeTemplateFromFolder,
    reloadFoldersBoard,
    refreshUnassigned,
  };
};
